import { PRESET_AGENDAS, SYSTEM_MEETING_TYPES, type MeetingSectionDef } from '@/modules/meetings/builtin-sections';
import { PRESET_TERMINOLOGY, renamedTermKeys, type TerminologyConfig } from '@/modules/terminology/types';
import { DEFAULT_VISION_TEMPLATE, PRESET_VISION_TEMPLATES, visionHasContent } from '@/modules/vision/vision-defaults';
import type { VisionData, VisionTemplate } from '@/modules/vision/template-types';
import { workspaceHasFeature, type SubscriptionTier } from '@/modules/workspaces/workspace-features';

export type PresetKey = 'eos' | 'scaling_up';

export interface BuiltInPreset {
  key: PresetKey;
  name: string;
  note: string;
}

export const BUILT_IN_PRESETS: BuiltInPreset[] = [
  {
    key: 'eos',
    name: 'EOS',
    note: 'V/TO and Measurables, the Level 10 Meeting, Quarterly Sessions, and Annual Planning.',
  },
  {
    key: 'scaling_up',
    name: 'Scaling Up',
    note: 'KPIs and Initiatives, with a vision template for your BHAG, brand promises, targets, and critical number.',
  },
];

export function isBuiltInPreset(key: string): key is PresetKey {
  return BUILT_IN_PRESETS.some((p) => p.key === key);
}

export type PresetCategory = 'terminology' | 'agendas' | 'vision';

export const PRESET_CATEGORIES: PresetCategory[] = ['terminology', 'agendas', 'vision'];

export type CategoryPlan =
  | { status: 'unchanged' }
  | { status: 'apply' }
  | { status: 'decide' }
  | { status: 'blocked' };

export interface PresetSettings {
  terminology: TerminologyConfig | null;
  terminologyPreset: string | null;
  agendaPreset: string | null;
  visionTemplate: VisionTemplate | null;
}

export interface PresetMeetingType {
  id?: string;
  key: string;
  builtIn: boolean;
  label: string | null;
  sections: MeetingSectionDef[] | null;
}

export interface PresetContext {
  settings: PresetSettings;
  meetingTypes: PresetMeetingType[];
  visions: { data: VisionData | null }[];
  tier: SubscriptionTier | null | undefined;
  isPersonal: boolean;
}

export function terminologyPresetFor(choice: PresetKey | null): PresetKey | null {
  return choice && PRESET_TERMINOLOGY[choice] ? choice : null;
}

export function agendaPresetFor(choice: PresetKey | null): PresetKey | null {
  return choice && PRESET_AGENDAS[choice] ? choice : null;
}

export function visionTemplateFor(choice: PresetKey | null): VisionTemplate | null {
  return (choice && PRESET_VISION_TEMPLATES[choice]) || null;
}

function sameSections(a: MeetingSectionDef[], b: MeetingSectionDef[]): boolean {
  return a.length === b.length && a.every((s, i) =>
    s.key === b[i].key && s.label === b[i].label && s.length === b[i].length && s.custom === b[i].custom);
}

function sameTemplate(a: VisionTemplate | null, b: VisionTemplate | null): boolean {
  return JSON.stringify(a ?? DEFAULT_VISION_TEMPLATE) === JSON.stringify(b ?? DEFAULT_VISION_TEMPLATE);
}

export function restatesDefaultLabel(type: PresetMeetingType): boolean {
  return type.label != null && type.label === SYSTEM_MEETING_TYPES[type.key]?.label;
}

export function restatesDefaultSections(type: PresetMeetingType): boolean {
  const system = SYSTEM_MEETING_TYPES[type.key]?.sections;
  return type.sections != null && !!system && sameSections(type.sections, system);
}

function isCustomized(type: PresetMeetingType): boolean {
  if (!type.builtIn || !(type.key in SYSTEM_MEETING_TYPES)) return false;
  const label = type.label != null && type.label.trim() !== '' && !restatesDefaultLabel(type);
  const sections = type.sections != null && !restatesDefaultSections(type);
  return label || sections;
}

function knownPreset(key: string | null, library: Record<string, unknown>): PresetKey | null {
  return key && key in library && isBuiltInPreset(key) ? key : null;
}

function layeredPlan(own: boolean, current: PresetKey | null, target: PresetKey | null): CategoryPlan {
  if (own) return { status: 'decide' };
  return current === target ? { status: 'unchanged' } : { status: 'apply' };
}

export function planPreset(choice: PresetKey | null, ctx: PresetContext): Record<PresetCategory, CategoryPlan> {
  const terminology = layeredPlan(
    renamedTermKeys(ctx.settings, ctx.tier, ctx.isPersonal).size > 0,
    knownPreset(ctx.settings.terminologyPreset, PRESET_TERMINOLOGY),
    terminologyPresetFor(choice),
  );

  const agendas = layeredPlan(
    workspaceHasFeature('meeting_types', ctx.tier, ctx.isPersonal) && ctx.meetingTypes.some(isCustomized),
    knownPreset(ctx.settings.agendaPreset, PRESET_AGENDAS),
    agendaPresetFor(choice),
  );

  const target = visionTemplateFor(choice);
  const vision: CategoryPlan = sameTemplate(ctx.settings.visionTemplate, target)
    ? { status: 'unchanged' }
    : ctx.visions.some((v) => visionHasContent(v.data)) ? { status: 'blocked' } : { status: 'apply' };

  return { terminology, agendas, vision };
}

export function hasDecision(plan: Record<PresetCategory, CategoryPlan>): boolean {
  return PRESET_CATEGORIES.some((c) => plan[c].status === 'decide');
}

export interface PresetWrite {
  terminology: boolean;
  clearTerms: boolean;
  agendas: boolean;
  clearLabels: string[];
  clearSections: string[];
  vision: boolean;
}

export function presetWrite(
  plan: Record<PresetCategory, CategoryPlan>,
  replace: Partial<Record<PresetCategory, boolean>>,
  ctx: PresetContext,
): PresetWrite {
  const writes = (c: PresetCategory) =>
    plan[c].status === 'apply' || (plan[c].status === 'decide' && !!replace[c]);
  const terminology = writes('terminology');
  const agendas = writes('agendas');
  const clearCustom = workspaceHasFeature('meeting_types', ctx.tier, ctx.isPersonal);
  const builtIns = ctx.meetingTypes.filter((t) => t.builtIn && t.id && t.key in SYSTEM_MEETING_TYPES);
  return {
    terminology,
    clearTerms: terminology && renamedTermKeys(ctx.settings, ctx.tier, ctx.isPersonal).size > 0,
    agendas,
    clearLabels: agendas
      ? builtIns.filter((t) => t.label != null && (clearCustom || restatesDefaultLabel(t))).map((t) => t.id!)
      : [],
    clearSections: agendas
      ? builtIns.filter((t) => t.sections != null && (clearCustom || restatesDefaultSections(t))).map((t) => t.id!)
      : [],
    vision: plan.vision.status === 'apply',
  };
}
